// src/components/settings/email-templates-tab.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import { Plus } from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { firestored } from "../../firebase/firebaseConfig";
import { useStateValue } from "../../context/StateProvider";
import { actionType } from "../../context/reducer";
import { DEFAULT_TEMPLATES } from "../../utils/openHouseEmails";
import { DEFAULT_ACCEPTANCE_TEMPLATES } from "../../utils/acceptanceEmails";
import { DEFAULT_TRANSACTION_TEMPLATES } from "../../utils/transactionEmailSequence";
import { useActionDialog } from "../ActionDialog";
import {
  saveEmailTemplate,
  deleteEmailTemplate,
  createCustomTemplate,
  loadEmailTemplates,
  isCustomTemplateId,
  customTemplatesFromMap,
  mergedCategoryList,
} from "../../utils/emailTemplateLoader";
import {
  cleanPastedText as cleanPasted,
  cleanPastedSubject,
  cleanTemplateDoc,
} from "../../utils/emailBrandTemplate";
import { normalizeGreetings } from "../../utils/emailPlaceholders";
import EmailTemplatesPicker, { BASE_CATEGORY_ORDER } from "./email-templates-picker";
import EmailTemplateForm from "./email-template-form";
import EmailTemplatePreview from "./email-template-preview";

function effectiveValues(templateId, customizations) {
  const d = DEFAULT_TEMPLATES[templateId] || DEFAULT_ACCEPTANCE_TEMPLATES[templateId] || DEFAULT_TRANSACTION_TEMPLATES[templateId] || {};
  const c = customizations || {};
  // cleanPasted: already-polluted saved bodies (raw pasted HTML) load as clean
  // text, so the editor shows readable copy and the next save persists it.
  // normalizeGreetings: a typed "Good Morning/Afternoon" loads as the
  // time-aware {{greeting}} token, and the next save persists that too.
  return {
    subject:  normalizeGreetings(cleanPastedSubject(c.subject ?? d.subject ?? "")),
    body:     normalizeGreetings(cleanPasted(c.body     ?? d.body     ?? "")),
    intro:    normalizeGreetings(cleanPasted(c.intro    ?? d.intro    ?? "")),
    closing:  normalizeGreetings(cleanPasted(c.closing  ?? d.closing  ?? "")),
    video:    c.video    ?? d.video    ?? null,
    label:    c.label    ?? "",
    category: c.category ?? "Custom",
    recipientRole: c.recipientRole ?? "client",
    trackOnDeals:  c.trackOnDeals === true,
  };
}

export default function EmailTemplatesTab() {
  const [{ user, emailTemplates }, dispatch] = useStateValue();
  const { confirm, dialog } = useActionDialog();
  const [selectedId, setSelectedId] = useState("scheduling-seller");
  const isRecap = selectedId === "recap-seller";
  const isCustom = isCustomTemplateId(selectedId);
  const saved = useMemo(
    () => effectiveValues(selectedId, emailTemplates?.[selectedId]),
    [selectedId, emailTemplates]
  );

  const [subject, setSubject] = useState(saved.subject);
  const [body, setBody] = useState(saved.body);
  const [intro, setIntro] = useState(saved.intro);
  const [closing, setClosing] = useState(saved.closing);
  const [video, setVideo] = useState(saved.video);
  const [label, setLabel] = useState(saved.label);
  const [category, setCategory] = useState(saved.category);
  const [recipientRole, setRecipientRole] = useState(saved.recipientRole);
  const [trackOnDeals, setTrackOnDeals] = useState(saved.trackOnDeals);
  const [saving, setSaving] = useState(false);

  // Default video only applies to transaction emails (the Email Center
  // composer reads it; open-house/acceptance senders don't).
  const isTransaction = !!DEFAULT_TRANSACTION_TEMPLATES[selectedId];

  // Custom templates (from state) + the dynamic category list they introduce.
  const { meta: customMeta } = useMemo(
    () => customTemplatesFromMap(emailTemplates),
    [emailTemplates]
  );
  const categoryOrder = useMemo(
    () => mergedCategoryList(BASE_CATEGORY_ORDER, emailTemplates),
    [emailTemplates]
  );

  // Sync drafts when the selected template (or its saved values) changes
  useEffect(() => {
    setSubject(saved.subject);
    setBody(saved.body);
    setIntro(saved.intro);
    setClosing(saved.closing);
    setVideo(saved.video);
    setLabel(saved.label);
    setCategory(saved.category);
    setRecipientRole(saved.recipientRole);
    setTrackOnDeals(saved.trackOnDeals);
  }, [selectedId, emailTemplates]);  // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty = subject !== saved.subject
    || (!isRecap && body !== saved.body)
    || (isRecap && (intro !== saved.intro || closing !== saved.closing))
    || ((isTransaction || isCustom) &&
        JSON.stringify(video || null) !== JSON.stringify(saved.video || null))
    || (isCustom && (label !== saved.label || category !== saved.category
        || recipientRole !== saved.recipientRole
        || trackOnDeals !== saved.trackOnDeals));

  const overridesForPreview = useMemo(() => {
    if (isRecap) return { subject, intro, closing };
    return { subject, body };
  }, [isRecap, subject, body, intro, closing]);

  const refresh = async () => {
    const fresh = await loadEmailTemplates();
    dispatch({ type: actionType.SET_EMAIL_TEMPLATES, payload: fresh });
    return fresh;
  };

  // Self-heal: the reducer cleans templates for on-screen use (SET_EMAIL_
  // TEMPLATES → cleanTemplateMap), but that only fixes what's in memory — the
  // Firestore doc itself stays polluted until something writes over it. When
  // a template opens, check the doc as it truly sits in Firestore and, if
  // it's still raw pasted HTML, silently persist the clean version once so
  // the database is fixed for real. Fire-and-forget: no toast, no spinner.
  const healedIdsRef = useRef(new Set());
  useEffect(() => {
    if (!user?.id || healedIdsRef.current.has(selectedId)) return;
    healedIdsRef.current.add(selectedId); // guard against re-firing/looping
    (async () => {
      try {
        const snap = await getDoc(doc(firestored, "emailTemplates", selectedId));
        if (!snap.exists()) return; // no saved doc yet — nothing to heal
        const raw = snap.data();
        const cleaned = cleanTemplateDoc(raw);
        if (cleaned === raw) return; // already clean
        const fields = {};
        for (const field of ["subject", "body", "intro", "closing"]) {
          if (field in cleaned) fields[field] = cleaned[field];
        }
        await saveEmailTemplate(selectedId, fields, user.id);
        await refresh();
      } catch (err) {
        console.error("Template self-heal failed:", err);
      }
    })();
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNew = async () => {
    setSaving(true);
    try {
      const id = await createCustomTemplate(
        { label: "Untitled Template", category: "Custom", subject: "", body: "" },
        user.id,
      );
      await refresh();
      setSelectedId(id);
      toast.success("New template created — give it a name and write your email.");
    } catch (err) {
      console.error(err);
      toast.error(`Couldn't create template: ${err.message || "unknown"}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (isCustom && !label.trim()) {
      toast.error("Give your template a name first.");
      return;
    }
    setSaving(true);
    try {
      // Safety net: if the draft still holds raw pasted HTML or a typed-out
      // greeting, persist the clean time-aware version instead. No-op for
      // drafts that are already clean.
      const cSubject = normalizeGreetings(cleanPastedSubject(subject));
      const cBody = normalizeGreetings(cleanPasted(body));
      let fields = isRecap
        ? {
            subject: cSubject,
            intro: normalizeGreetings(cleanPasted(intro)),
            closing: normalizeGreetings(cleanPasted(closing)),
          }
        : { subject: cSubject, body: cBody };
      if (isTransaction) fields.video = video || null;
      if (isCustom) {
        fields = {
          custom: true,
          label: label.trim(),
          category: (category || "Custom").trim() || "Custom",
          recipientRole: recipientRole || "client",
          trackOnDeals: trackOnDeals === true,
          subject: cSubject,
          body: cBody,
          video: video || null,
        };
      }
      await saveEmailTemplate(selectedId, fields, user.id);
      await refresh();
      toast.success("Template saved.");
    } catch (err) {
      console.error(err);
      toast.error(`Save failed: ${err.message || "unknown"}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setSubject(saved.subject);
    setBody(saved.body);
    setIntro(saved.intro);
    setClosing(saved.closing);
    setVideo(saved.video);
    setLabel(saved.label);
    setCategory(saved.category);
    setRecipientRole(saved.recipientRole);
    setTrackOnDeals(saved.trackOnDeals);
  };

  const handleResetOrDelete = async () => {
    const ok = await confirm({
      title: isCustom ? "Delete template" : "Reset template",
      body: isCustom
        ? "Delete this custom template?"
        : "Reset this template to its default text?",
      note: isCustom
        ? "This can't be undone."
        : "Your customizations will be deleted.",
      tone: "danger",
      confirmLabel: isCustom ? "Delete" : "Reset",
    });
    if (!ok) return;
    setSaving(true);
    try {
      await deleteEmailTemplate(selectedId);
      await refresh();
      if (isCustom) setSelectedId("scheduling-seller");
      toast.success(isCustom ? "Template deleted." : "Reset to default.");
    } catch (err) {
      console.error(err);
      toast.error(`${isCustom ? "Delete" : "Reset"} failed: ${err.message || "unknown"}`);
    } finally {
      setSaving(false);
    }
  };

  const modifiedIds = Object.keys(emailTemplates || {});

  return (
    <div className="bg-slate-50 rounded-lg overflow-hidden border border-slate-200">
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-3">
        <div className="text-sm font-semibold text-slate-700">Email Templates</div>
        <span className="bg-slate-100 text-linkColor text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded">Admins · Supervisors</span>
        <button
          type="button"
          onClick={handleNew}
          disabled={saving}
          className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-slate-800 rounded px-3 py-1.5 hover:bg-slate-900 disabled:opacity-50"
        >
          <Plus size={13} /> New Template
        </button>
        <span className="flex-1"></span>
        <span className="text-xs text-slate-400">Team-wide. Changes affect emails sent by everyone.</span>
      </div>

      <div className="grid grid-cols-[220px_1fr_1fr] min-h-[600px]">
        <EmailTemplatesPicker
          selectedId={selectedId}
          onSelect={setSelectedId}
          modifiedIds={modifiedIds}
          customMeta={customMeta}
          categoryOrder={categoryOrder}
        />
        <EmailTemplateForm
          templateId={selectedId}
          isRecap={isRecap}
          subject={subject} onSubjectChange={setSubject}
          body={body} onBodyChange={setBody}
          intro={intro} onIntroChange={setIntro}
          closing={closing} onClosingChange={setClosing}
          showVideo={isTransaction || isCustom}
          video={video} onVideoChange={setVideo}
          isCustom={isCustom}
          label={label} onLabelChange={setLabel}
          category={category} onCategoryChange={setCategory}
          categoryOptions={categoryOrder}
          recipientRole={recipientRole} onRecipientRoleChange={setRecipientRole}
          trackOnDeals={trackOnDeals} onTrackOnDealsChange={setTrackOnDeals}
        />
        <EmailTemplatePreview
          templateId={selectedId}
          sender={user}
          overrides={overridesForPreview}
        />
      </div>

      <div className="flex items-center gap-2 px-5 py-3 bg-slate-50 border-t border-slate-200">
        <button
          type="button"
          onClick={handleResetOrDelete}
          disabled={saving}
          className="text-xs font-semibold text-red-700 border border-red-200 rounded px-3 py-1.5 hover:bg-red-50 disabled:opacity-50"
        >
          {isCustom ? "Delete template" : "Reset to default"}
        </button>
        <span className="flex-1"></span>
        <button
          type="button"
          onClick={handleDiscard}
          disabled={saving || !isDirty}
          className="text-xs font-semibold text-slate-600 border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-100 disabled:opacity-50"
        >
          Discard changes
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !isDirty}
          className="text-xs font-semibold text-white bg-slate-800 rounded px-3 py-1.5 hover:bg-slate-900 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      {dialog}
    </div>
  );
}
